import { h } from 'preact'
import { useEffect, useState } from 'preact/hooks'
import { effect } from '@preact/signals-core'
import { createPortal } from 'preact/compat'
import {
  trash_drive_index_path,
  shares_drive_index_path,
  shared_with_me_drive_index_path,
  statistics_drive_index_path,
  webdav_drive_index_path
} from '../../routes'
import { useDriveStore, useDriveI18n, useDriveUi } from './drive-context.js'
import { openNewDriveFile } from '../../modules/drive/doc-editor.js'
import { QuotaBanner } from '../shared/quota/index.js'
import { metricLabel } from '../shared/quota/quota-i18n.js'

export function DriveToolbar() {
  const store = useDriveStore()
  const i18n = useDriveI18n()
  const ui = useDriveUi()
  const t = (key, opts) => i18n.t(key, opts)

  const [selectedCount, setSelectedCount] = useState(store.selectedIds.value.length)
  const [virtual, setVirtual] = useState(false)

  useEffect(() => effect(() => {
    setSelectedCount(store.selectedIds.value.length)
    const folder = store.currentFolder.value
    setVirtual(ui.isVirtualFolder?.(folder) ?? false)
  }), [store, ui])

  const selected = selectedCount > 0
  const storageBlocked = !!ui.storageHardStopExhausted
  const slot = typeof document !== 'undefined'
    ? document.getElementById('drive-toolbar-slot')
    : null

  const handleUploadFiles = () => {
    if (storageBlocked) {
      ui.openQuotaModal?.()
      return
    }
    ui.triggerUploadFiles?.()
  }

  const handleUploadFolder = () => {
    if (storageBlocked) {
      ui.openQuotaModal?.()
      return
    }
    ui.triggerUploadFolder?.()
  }

  const blockedTitle = storageBlocked
    ? t('quotas.limit_reached', { metric: metricLabel(ui.storageQuota?.metric, t) })
    : undefined

  const toolbar = (
    <div
      id="drive-toolbar-controller"
      class={`drive-toolbar d-flex gap-1 align-items-center flex-nowrap${virtual ? ' drive-virtual-folder' : ''}`}
    >
      <div class={`d-flex gap-1 align-items-center flex-nowrap${selected ? ' d-none' : ''}`}>
        {ui.canCreate && (
          <div class="btn-group">
            <button
              type="button"
              class={`btn btn-primary${storageBlocked ? ' disabled' : ''}`}
              aria-disabled={storageBlocked ? 'true' : undefined}
              title={blockedTitle}
              onClick={handleUploadFiles}
            >
              <i class={`bi ${storageBlocked ? 'bi-lock-fill' : 'bi-cloud-arrow-up'}`} />
              <span class="d-none d-md-inline ms-1">{t('drive.upload_files')}</span>
            </button>
            <button
              type="button"
              class={`btn btn-primary dropdown-toggle dropdown-toggle-split${storageBlocked ? ' disabled' : ''}`}
              data-bs-toggle="dropdown"
              aria-expanded="false"
              aria-disabled={storageBlocked ? 'true' : undefined}
              title={blockedTitle}
              onClick={storageBlocked ? (e) => { e.preventDefault(); e.stopPropagation(); ui.openQuotaModal?.() } : undefined}
            >
              <span class="visually-hidden">{t('helpers.toggle_dropdown')}</span>
            </button>
            {!storageBlocked && (
              <ul class="dropdown-menu dropdown-menu-end">
                <li>
                  <button class="dropdown-item" type="button" onClick={handleUploadFiles}>
                    <i class="bi bi-file-earmark-arrow-up me-2" />{t('drive.upload_files')}
                  </button>
                </li>
                <li>
                  <button class="dropdown-item" type="button" onClick={handleUploadFolder}>
                    <i class="bi bi-folder-symlink me-2" />{t('drive.upload_folder')}
                  </button>
                </li>
              </ul>
            )}
          </div>
        )}

        {(ui.canCreate || ui.editorEnabled) && (
          <div class="btn-group drive-new-actions">
            <button type="button" class="btn btn-outline-primary dropdown-toggle" data-bs-toggle="dropdown" aria-expanded="false">
              <i class="bi bi-plus-lg" />
              <span class="d-none d-md-inline ms-1">{t('drive.new')}</span>
            </button>
            <ul class="dropdown-menu dropdown-menu-end">
              {ui.canCreate && (
                <li class="drive-new-folder-action">
                  <button type="button" class="dropdown-item" onClick={() => ui.openNewFolder?.()}>
                    <i class="bi bi-folder-plus me-2" />{t('drive.new_folder')}
                  </button>
                </li>
              )}
              {ui.editorEnabled && ui.onlyOfficeEntitled && (
                <>
                  {ui.canCreate && <li><hr class="dropdown-divider" /></li>}
                  {['docx', 'xlsx', 'pptx'].map((ft) => (
                    <li key={ft}>
                      <button
                        class="dropdown-item"
                        type="button"
                        onClick={() => openNewDriveFile({ fileType: ft })}
                      >
                        <i class={`bi me-2 ${ft === 'docx' ? 'bi-file-earmark-word text-primary' : ft === 'xlsx' ? 'bi-file-earmark-spreadsheet text-success' : 'bi-file-earmark-slides text-warning'}`} />
                        {t(ft === 'docx' ? 'drive.new_word' : ft === 'xlsx' ? 'drive.new_excel' : 'drive.new_powerpoint')}
                      </button>
                    </li>
                  ))}
                </>
              )}
              {ui.editorEnabled && !ui.onlyOfficeEntitled && (
                <>
                  {ui.canCreate && <li><hr class="dropdown-divider" /></li>}
                  <li>
                    <span class="dropdown-item-text text-muted small d-flex align-items-center gap-2">
                      <i class="bi bi-lock-fill text-warning" />
                      {ui.onlyOfficeLockTitle}
                      {ui.onlyOfficeUpgradePath && (
                        <a href={ui.onlyOfficeUpgradePath} class="ms-1">{t('entitlements.view_plans')}</a>
                      )}
                    </span>
                  </li>
                </>
              )}
            </ul>
          </div>
        )}

        <div class="btn-group">
          <button
            type="button"
            class="btn btn-outline-secondary dropdown-toggle"
            data-bs-toggle="dropdown"
            data-bs-auto-close="true"
            aria-expanded="false"
            aria-label={t('drive.more_actions')}
          >
            <i class="bi bi-three-dots-vertical" />
          </button>
          <ul class="dropdown-menu dropdown-menu-end">
            <li><a class="dropdown-item" href={trash_drive_index_path()}><i class="bi bi-trash me-2" />{t('drive.trash')}</a></li>
            <li><a class="dropdown-item" href={shares_drive_index_path()}><i class="bi bi-share me-2" />{t('drive.my_shares')}</a></li>
            <li><a class="dropdown-item" href={shared_with_me_drive_index_path()}><i class="bi bi-people-fill me-2" />{t('drive.shared_with_me')}</a></li>
            <li><hr class="dropdown-divider" /></li>
            <li><a class="dropdown-item" href={statistics_drive_index_path()}><i class="bi bi-bar-chart-line me-2" />{t('drive.statistics.nav')}</a></li>
            {ui.webdavEnabled && (
              <li><a class="dropdown-item" href={webdav_drive_index_path()}><i class="bi bi-hdd-network me-2" />{t('drive.webdav.nav')}</a></li>
            )}
          </ul>
        </div>

        {ui.storageQuota && (
          <QuotaBanner quota={ui.storageQuota} t={t} format="bytes" compact threshold={0.9} />
        )}
      </div>

      <div class={`${selected ? 'd-flex' : 'd-none'} gap-1 align-items-center flex-nowrap`}>
        <button type="button" class="btn btn-outline-secondary" onClick={() => store.clearSelection()} title={t('drive.deselect_all')} aria-label={t('drive.deselect_all')}>
          <i class="bi bi-x-lg" aria-hidden="true" />
        </button>
        <span class="text-muted text-nowrap small me-1">
          {selectedCount} {t('drive.items_selected')}
        </span>
        <button type="button" class="btn btn-outline-secondary" onClick={() => ui.downloadSelected?.()} title={t('drive.download')}>
          <i class="bi bi-download" />
          <span class="d-none d-lg-inline ms-1">{t('drive.download')}</span>
        </button>
        <button
          type="button"
          class="btn btn-outline-secondary"
          onClick={() => ui.shareSelected?.()}
          title={t('drive.share_this')}
          disabled={selectedCount !== 1}
        >
          <i class="bi bi-share" />
          <span class="d-none d-lg-inline ms-1">{t('drive.share_this')}</span>
        </button>
        {ui.canUpdate && (
          <button type="button" class="btn btn-outline-secondary" onClick={() => ui.openMove?.()} title={t('drive.move')}>
            <i class="bi bi-folder-symlink" />
            <span class="d-none d-lg-inline ms-1">{t('drive.move')}</span>
          </button>
        )}
        {ui.canDestroy && (
          <button type="button" class="btn btn-outline-danger" onClick={() => ui.deleteSelected?.()} title={t('drive.delete')}>
            <i class="bi bi-trash" />
            <span class="d-none d-lg-inline ms-1">{t('drive.delete')}</span>
          </button>
        )}
      </div>
    </div>
  )

  if (slot) return createPortal(toolbar, slot)
  return toolbar
}
